import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { API } from "@/App";
import { toast } from "sonner";
import {
  Users, Trophy, Layers, Radio, Calendar, Globe,
  Mail, Lock, ArrowLeft, ArrowRight,
} from "lucide-react";

/* Brand tokens — aligned with LandingHome + ClubPublicPage */
const NAVY = "#0B1D33";
const OFF_WHITE = "#F4F1EC";
const CREAM = "#F7F2E8";
const FAIRWAY = "#2E4834";
const FAIRWAY_ACCENT = "#3F6B4A";
const SUNBURST = "#E8C76A";
const STONE = "#8A867E";
const BURGUNDY = "#6E1F2B";
const FONT_STACK = "'Hanken Grotesk', system-ui, -apple-system, sans-serif";
const FONT_SCRIPT = "'Caveat', 'Dancing Script', cursive";

const SERVICES = [
  {
    icon: Users,
    title: "Padrón de jugadores",
    desc: "Da de alta y administra a todos los socios de tu club con handicap, GHIN y categorías.",
  },
  {
    icon: Trophy,
    title: "Torneos",
    desc: "Crea torneos Medal, Stableford o Match Play; inscribe categorías y publica leaderboards.",
  },
  {
    icon: Layers,
    title: "Rankings anuales",
    desc: "Puntuación acumulada por temporadas, con etapas y podios por categoría.",
  },
  {
    icon: Radio,
    title: "Score en vivo",
    desc: "Tus jugadores capturan tarjeta desde la app y el marcador se actualiza al instante.",
  },
  {
    icon: Calendar,
    title: "Tee times",
    desc: "Reserva de horarios, control de cupo y notificaciones automáticas por push.",
  },
  {
    icon: Globe,
    title: "Sitio público",
    desc: "Tu club tiene su microsite `cadii.app/tu-club` con torneos en vivo y padrón.",
  },
];

export default function CadiihouseLanding() {
  return (
    <div style={{ backgroundColor: CREAM, color: NAVY, fontFamily: FONT_STACK, minHeight: "100vh" }}>
      <TopBar />
      <Hero />
      <Services />
      <ClosingBanner />
      <FooterBar />
    </div>
  );
}

/* ─── Top bar ─── */
function TopBar() {
  return (
    <nav
      className="sticky top-0 z-40"
      style={{
        backgroundColor: "rgba(247,242,232,0.92)",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
        borderBottom: `1px solid rgba(11,29,51,0.08)`,
      }}
    >
      <div className="max-w-[1180px] mx-auto flex items-center justify-between px-6 lg:px-10 py-4">
        <Link to="/" className="flex items-baseline gap-2" data-testid="ch-nav-home">
          <span style={{ fontFamily: FONT_SCRIPT, color: NAVY, fontSize: 32, fontWeight: 700, lineHeight: 1 }}>cadii</span>
          <span
            className="hidden sm:inline"
            style={{ color: NAVY, fontSize: 9, letterSpacing: "1.6px", fontWeight: 800, lineHeight: 1 }}
          >
            GOLF<br />CLUB
          </span>
        </Link>
        <div className="flex items-center gap-3 sm:gap-5">
          <Link
            to="/"
            className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold"
            style={{ color: NAVY }}
            data-testid="ch-nav-back"
          >
            <ArrowLeft className="w-4 h-4" /> Volver
          </Link>
          <a
            href="#acceso"
            className="rounded-full px-5 py-2.5"
            style={{ backgroundColor: NAVY, color: OFF_WHITE, fontSize: 13, fontWeight: 800, letterSpacing: "-0.1px" }}
            data-testid="ch-nav-login"
          >
            Ingresar a CadiiHouse
          </a>
        </div>
      </div>
    </nav>
  );
}

/* ─── Hero with inline login ─── */
function Hero() {
  return (
    <section id="acceso" className="relative">
      <div className="max-w-[1180px] mx-auto px-6 lg:px-10 py-14 sm:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-10 lg:gap-14 items-start">
          {/* Left: pitch */}
          <div>
            <p style={{ color: FAIRWAY, fontSize: 12, letterSpacing: "3px", fontWeight: 800, textTransform: "uppercase" }}>
              CadiiHouse · Para clubes
            </p>
            <h1 className="mt-3 font-black leading-[1.02]"
                style={{ fontSize: "clamp(38px, 6vw, 68px)", letterSpacing: "-1.4px" }}>
              La casa digital de tu <span style={{ fontFamily: FONT_SCRIPT, fontWeight: 700, color: FAIRWAY }}>club de golf</span>.
            </h1>
            <p className="mt-5 max-w-xl" style={{ fontSize: 17, lineHeight: 1.55, color: STONE, fontWeight: 500 }}>
              CadiiHouse es la plataforma que usan los clubes en cadii para administrar su padrón, organizar torneos, correr rankings y transmitir cada ronda en vivo desde un solo lugar.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a
                href="mailto:hello@cadii.app?subject=Quiero%20activar%20CadiiHouse%20en%20mi%20club"
                className="inline-flex items-center gap-2 rounded-full px-6 py-3"
                style={{ backgroundColor: FAIRWAY_ACCENT, color: OFF_WHITE, fontSize: 14, fontWeight: 800, letterSpacing: "-0.1px" }}
                data-testid="ch-demo-btn"
              >
                Solicitar demo <ArrowRight className="w-4 h-4" />
              </a>
              <span style={{ color: STONE, fontSize: 13, fontWeight: 600 }}>
                o inicia sesión si tu club ya está dado de alta →
              </span>
            </div>
          </div>

          {/* Right: login card */}
          <LoginCard />
        </div>
      </div>
    </section>
  );
}

function LoginCard() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password || loading) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/club/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: email.trim(), password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Credenciales inválidas");
      if (data.session_token) localStorage.setItem("cadii_club_token", data.session_token);
      localStorage.setItem("cadii_club_user", JSON.stringify(data));
      toast.success(`Bienvenido a ${data.club?.name || "tu CadiiHouse"}`);
      navigate("/club", { replace: true });
    } catch (err) {
      toast.error(typeof err.message === "string" ? err.message : "Error de autenticación");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="p-7"
      style={{
        backgroundColor: NAVY, color: OFF_WHITE,
        borderRadius: 18, boxShadow: "0 20px 60px rgba(11,29,51,0.20)",
      }}
      data-testid="ch-login-card"
    >
      <p style={{ color: SUNBURST, fontSize: 11, letterSpacing: "3px", fontWeight: 800, textTransform: "uppercase" }}>
        Acceso CadiiHouse
      </p>
      <h2 className="mt-2 font-black" style={{ fontSize: 26, letterSpacing: "-0.5px", lineHeight: 1.15 }}>
        Ingresa a tu club
      </h2>
      <p className="mt-1" style={{ color: "rgba(244,241,236,0.65)", fontSize: 13, fontWeight: 500 }}>
        Usa las credenciales que le compartiste al equipo de cadii.
      </p>

      <div className="mt-6 space-y-3">
        <label className="block">
          <span style={{ fontSize: 11, letterSpacing: "1.6px", fontWeight: 800, color: "rgba(244,241,236,0.6)", textTransform: "uppercase" }}>
            Email
          </span>
          <div className="mt-1.5 flex items-center gap-2 rounded-xl px-3"
               style={{ backgroundColor: "rgba(244,241,236,0.08)", border: "1px solid rgba(244,241,236,0.14)" }}>
            <Mail className="w-4 h-4" style={{ color: SUNBURST }} />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="tu@club.com"
              required
              data-testid="ch-email"
              style={{
                flex: 1, backgroundColor: "transparent", border: "none", outline: "none",
                color: OFF_WHITE, fontSize: 14, fontWeight: 600,
                padding: "12px 4px", fontFamily: FONT_STACK,
              }}
            />
          </div>
        </label>

        <label className="block">
          <span style={{ fontSize: 11, letterSpacing: "1.6px", fontWeight: 800, color: "rgba(244,241,236,0.6)", textTransform: "uppercase" }}>
            Contraseña
          </span>
          <div className="mt-1.5 flex items-center gap-2 rounded-xl px-3"
               style={{ backgroundColor: "rgba(244,241,236,0.08)", border: "1px solid rgba(244,241,236,0.14)" }}>
            <Lock className="w-4 h-4" style={{ color: SUNBURST }} />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Tu contraseña"
              required
              data-testid="ch-password"
              style={{
                flex: 1, backgroundColor: "transparent", border: "none", outline: "none",
                color: OFF_WHITE, fontSize: 14, fontWeight: 600,
                padding: "12px 4px", fontFamily: FONT_STACK,
              }}
            />
          </div>
        </label>
      </div>

      <button
        type="submit"
        disabled={loading}
        className="mt-6 w-full rounded-full py-3 font-black"
        style={{
          backgroundColor: loading ? "rgba(232,199,106,0.5)" : SUNBURST,
          color: NAVY, fontSize: 14, letterSpacing: "-0.1px",
        }}
        data-testid="ch-login-submit"
      >
        {loading ? "Ingresando…" : "Entrar a CadiiHouse"}
      </button>

      <p className="mt-4 text-center" style={{ fontSize: 12, color: "rgba(244,241,236,0.55)", fontWeight: 500 }}>
        ¿Sin cuenta?{" "}
        <a href="mailto:hello@cadii.app?subject=Alta%20CadiiHouse" style={{ color: SUNBURST, fontWeight: 700 }}>
          Contáctanos
        </a>{" "}
        y damos de alta a tu club.
      </p>
    </form>
  );
}

/* ─── Services ─── */
function Services() {
  return (
    <section id="servicios" className="pt-4 pb-14">
      <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
        <div className="flex items-center gap-3 mb-8">
          <span className="inline-block w-6 h-px" style={{ backgroundColor: FAIRWAY }} />
          <span style={{ color: FAIRWAY, fontSize: 11, letterSpacing: "3px", fontWeight: 800, textTransform: "uppercase" }}>
            ¿Qué incluye?
          </span>
        </div>
        <h2 className="font-black mb-2" style={{ fontSize: "clamp(28px, 3.6vw, 40px)", letterSpacing: "-0.9px" }}>
          Todo lo que tu club necesita para operar.
        </h2>
        <p className="max-w-2xl mb-10" style={{ color: STONE, fontSize: 15.5, fontWeight: 500, lineHeight: 1.55 }}>
          Sin instalaciones, sin licencias por jugador y sin capacitación técnica: activamos tu CadiiHouse en un día.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="ch-services-grid">
          {SERVICES.map((s) => {
            const Icon = s.icon;
            return (
              <div
                key={s.title}
                className="p-6"
                style={{
                  backgroundColor: OFF_WHITE, borderRadius: 16,
                  border: "1px solid rgba(11,29,51,0.06)",
                }}
                data-testid={`ch-service-${s.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
                  style={{ backgroundColor: "rgba(46,72,52,0.10)" }}
                >
                  <Icon className="w-5 h-5" style={{ color: FAIRWAY }} strokeWidth={1.6} />
                </div>
                <h3 className="font-black" style={{ fontSize: 17, letterSpacing: "-0.2px" }}>{s.title}</h3>
                <p className="mt-1.5" style={{ color: STONE, fontSize: 14, fontWeight: 500, lineHeight: 1.5 }}>
                  {s.desc}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ─── Closing banner ─── */
function ClosingBanner() {
  return (
    <section className="pb-16">
      <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
        <div
          className="p-8 sm:p-12 flex flex-col sm:flex-row items-start sm:items-center gap-6 justify-between"
          style={{ backgroundColor: NAVY, color: OFF_WHITE, borderRadius: 20 }}
        >
          <div>
            <p style={{ color: SUNBURST, fontSize: 11, letterSpacing: "3px", fontWeight: 800, textTransform: "uppercase" }}>
              ¿Interesado?
            </p>
            <h3 className="mt-2 font-black" style={{ fontSize: "clamp(24px, 3vw, 34px)", letterSpacing: "-0.6px", lineHeight: 1.15 }}>
              Activamos CadiiHouse en tu club esta semana.
            </h3>
            <p className="mt-2" style={{ color: "rgba(244,241,236,0.7)", fontSize: 15, fontWeight: 500 }}>
              Escríbenos y te mostramos una demo con datos reales.
            </p>
          </div>
          <a
            href="mailto:hello@cadii.app?subject=Quiero%20activar%20CadiiHouse%20en%20mi%20club"
            className="inline-flex items-center gap-2 rounded-full px-6 py-3 flex-shrink-0"
            style={{ backgroundColor: SUNBURST, color: NAVY, fontSize: 14, fontWeight: 800, letterSpacing: "-0.1px" }}
            data-testid="ch-closing-demo"
          >
            Solicitar demo <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
}

/* ─── Footer ─── */
function FooterBar() {
  return (
    <footer className="py-10 px-6" style={{ backgroundColor: NAVY, color: OFF_WHITE }} data-testid="site-footer">
      <div className="max-w-[1180px] mx-auto flex flex-col items-center gap-5">
        <Link to="/" className="inline-flex items-baseline gap-1.5">
          <span style={{ fontFamily: FONT_SCRIPT, fontSize: 34, fontWeight: 700, lineHeight: 1 }}>cadii</span>
          <span style={{ fontSize: 9, letterSpacing: "1.6px", fontWeight: 800 }}>GOLF<br />CLUB</span>
        </Link>
        <div className="flex flex-wrap items-center justify-center gap-4 text-xs" style={{ color: "rgba(244,241,236,0.7)" }}>
          <Link to="/privacy" className="hover:text-white">Privacy Policy</Link>
          <span aria-hidden>·</span>
          <Link to="/terms" className="hover:text-white">Terms of Service</Link>
          <span aria-hidden>·</span>
          <Link to="/contact" className="hover:text-white">Contact</Link>
        </div>
        <p
          className="text-center"
          style={{ color: "rgba(244,241,236,0.65)", fontSize: 12, letterSpacing: 0.2, lineHeight: 1.5, maxWidth: 720 }}
          data-testid="legal-entity-line"
        >
          CADii es operado por <strong style={{ color: OFF_WHITE }}>Multigoods Inmuebles, S. de R.L. de C.V.</strong>
          <br className="hidden sm:block" />
          CADii is operated by <strong style={{ color: OFF_WHITE }}>Multigoods Inmuebles, S. de R.L. de C.V.</strong>
        </p>
        <p style={{ color: "rgba(244,241,236,0.45)", fontSize: 11, letterSpacing: 0.4 }}>
          © {new Date().getFullYear()} Multigoods Inmuebles, S. de R.L. de C.V. · Todos los derechos reservados
        </p>
      </div>
    </footer>
  );
}
